import { IconNames } from '@blueprintjs/icons';
import { capitalizeString, DateFormats, formatDate } from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { InfoDialog } from '@strata/react-dialog';
import { FileUploadFormik } from '@strata/react-file-upload';
import { FormContainer } from '@strata/react-form-container';
import { FormHeaderAndContent } from '@strata/react-form-header-and-content';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { Grid } from '@strata/react-grid';
import { InlineMessage } from '@strata/react-inline-message';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useState } from 'react';
import { Navigate } from 'react-router';
import * as Yup from 'yup';

import { addBulkAdHocPerformance } from 'api/performanceAPI';
import { handleError } from 'services/Error';
import { AppRoutes } from 'services/routes';
import { useGetAdHocValueCasesQuery } from 'store/api/performanceApi';
import { useGetPayerSegmentsQuery } from 'store/api/referenceApi';

const ReasonOptions = ['Missed at Auction', 'Payer Request', 'Other'];

const errorColumns = [
    { header: 'Row', accessorKey: 'row_number', size: 80 },
    { header: 'Error', accessorKey: 'error', size: 400 },
];

const validationErrorsFromResponse = (error) => {
    try {
        const detail = JSON.parse(error?.message ?? '{}')?.detail;
        const uploadErrors = (() => {
            if (Array.isArray(detail)) {
                return detail;
            }
            if (typeof detail === 'string' && detail) {
                return [{ error_message: detail, invalid_rows: [] }];
            }
            return undefined;
        })();
        if (!uploadErrors) {
            return undefined;
        }
        return uploadErrors.flatMap((uploadError) =>
            uploadError.invalid_rows?.length
                ? uploadError.invalid_rows.map((row) => ({
                      row_number: row.row_number,
                      error: row.error,
                  }))
                : [{ row_number: '', error: uploadError.error_message }],
        );
    } catch {
        return undefined;
    }
};

const downloadErrors = (validationErrors) => {
    const csvHeader = `${Object.keys(validationErrors[0])
        .map((header) => capitalizeString(header))
        .join(',')}\n`;
    const csvBody = validationErrors.map((row) => Object.values(row).join(',')).join('\n');
    const hiddenElement = document.createElement('a');
    hiddenElement.href = `data:text/csv;charset=utf-8,${encodeURI(csvHeader + csvBody)}`;
    hiddenElement.target = '_blank';
    hiddenElement.download = 'Referral Upload Errors.csv';
    hiddenElement.click();
};

export const BulkAttachAdHocValueCase = () => {
    const { showAddAdHocValueCaseAgora = false } = useFlags() ?? {};
    const { data: segments, isFetching: segmentsFetching } = useGetPayerSegmentsQuery();
    const { payerSegmentList } = segments ?? {};
    const { data: valueCases, isFetching: valueCasesFetching } = useGetAdHocValueCasesQuery(
        undefined,
        { skip: !showAddAdHocValueCaseAgora },
    );
    const [validationErrors, setValidationErrors] = useState();
    const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);

    if (!showAddAdHocValueCaseAgora) {
        return <Navigate replace to={AppRoutes.home} />;
    }

    return (
        <FormHeaderAndContent
            mainContent={
                <Formik
                    initialValues={{}}
                    validationSchema={Yup.object({
                        segment: Yup.object().required(
                            validationStrings.requiredField.errorMessage,
                        ),
                        valueCase: Yup.object().required(
                            validationStrings.requiredField.errorMessage,
                        ),
                        reason: Yup.string().required(validationStrings.requiredField.errorMessage),
                        referralFile: Yup.array()
                            .required(validationStrings.requiredField.errorMessage)
                            .min(1, validationStrings.requiredField.errorMessage),
                    })}
                    onSubmit={async (values, { resetForm }) => {
                        try {
                            setValidationErrors(undefined);
                            setShowValidationErrorDialog(false);
                            await addBulkAdHocPerformance({
                                payerId: values.segment.payerId,
                                segmentId: values.segment.secondaryData,
                                reason: values.reason,
                                valueCaseName: values.valueCase.name,
                                performanceStartDate: formatDate(dayjs(), DateFormats.HYPHEN_DATE),
                                file: values.fileToUpload,
                            });
                            SuccessToast({
                                message: 'Value case attached to specified referrals.',
                            });
                            resetForm();
                        } catch (err) {
                            const errors = validationErrorsFromResponse(err);
                            if (errors) {
                                setValidationErrors(errors);
                                handleError(
                                    'Value case was not attached. Review the errors and try again.',
                                    err,
                                );
                            } else {
                                handleError(
                                    'An unexpected error occurred while attaching the value case.',
                                    err,
                                );
                            }
                        }
                    }}
                >
                    {(formik) => {
                        const valueCaseOptions = (valueCases ?? [])
                            .filter(
                                (valueCase) =>
                                    valueCase.segmentId === formik.values.segment?.secondaryData,
                            )
                            .map(({ valueCaseName, valueCaseId }) => ({
                                name: valueCaseName,
                                value: valueCaseId,
                            }));

                        return (
                            <FormContainer
                                formTitle="Bulk Attach Ad Hoc Value Case"
                                primaryButtonLabel="Attach Value Case"
                                onFormSubmit={formik.handleSubmit}
                                onCancelClick={() => {
                                    setValidationErrors(undefined);
                                    setShowValidationErrorDialog(false);
                                    formik.resetForm();
                                }}
                                isSubmitting={
                                    formik.isSubmitting ||
                                    !!validationErrors ||
                                    !(formik.isValid && formik.dirty)
                                }
                                formLeftContent={
                                    <>
                                        <FullscreenLoading
                                            isShowing={formik.isSubmitting}
                                            loadingText="Attaching value cases..."
                                        />
                                        <div className="margin__bottom--medium">
                                            <InlineMessage
                                                intent={InlineMessage.Intent.INFO}
                                                wrapText
                                                label="This action will attach an ad hoc value case to the referrals listed. The referrals must all be ACTIVE, be associated with the indicated payer segment, and must not already have the indicated value case attached. Administrative Services Only referrals are rejected."
                                            />
                                        </div>
                                        <SuggestSingleSelectFormik
                                            id="segment"
                                            label="Payer Segment"
                                            required
                                            isLoading={segmentsFetching}
                                            values={payerSegmentList}
                                            onChange={async (newValue) => {
                                                await formik.setValues({
                                                    ...formik.values,
                                                    segment: newValue,
                                                    valueCase: undefined,
                                                });
                                            }}
                                        />
                                        <SuggestSingleSelectFormik
                                            id="valueCase"
                                            label="Value Case"
                                            required
                                            disabled={!formik.values.segment}
                                            isLoading={valueCasesFetching}
                                            values={valueCaseOptions}
                                        />
                                        <SuggestSingleSelectFormik
                                            id="reason"
                                            label="Reason"
                                            required
                                            values={ReasonOptions}
                                        />
                                        <InfoDialog
                                            dialogTitle="Bulk Upload Errors"
                                            isOpen={showValidationErrorDialog}
                                            onClose={() => setShowValidationErrorDialog(false)}
                                            width={800}
                                            dialogContent={
                                                <>
                                                    <div
                                                        className="flex__gap--small margin__bottom--medium"
                                                        style={{ justifyContent: 'space-between' }}
                                                    >
                                                        <span>
                                                            Below are the errors that occurred while
                                                            uploading the referral file.
                                                        </span>
                                                        <ActionButtonText
                                                            label="Download Errors"
                                                            leftIcon={IconNames.IMPORT}
                                                            onClick={() =>
                                                                downloadErrors(validationErrors)
                                                            }
                                                        />
                                                    </div>
                                                    <Grid
                                                        data={validationErrors}
                                                        columns={errorColumns}
                                                        sortProperties={[
                                                            { id: 'row_number', desc: false },
                                                        ]}
                                                    />
                                                </>
                                            }
                                        />
                                        <FileUploadFormik
                                            id="referralFile"
                                            label="Referral File"
                                            required
                                            accept=".csv,.xlsx"
                                            maxAllowedFiles={1}
                                            helperText="CSV or Excel file with a single referral_id column."
                                            onAddFiles={(files) => {
                                                setValidationErrors(undefined);
                                                setShowValidationErrorDialog(false);
                                                formik.setFieldValue('fileToUpload', files?.[0]);
                                            }}
                                            onRemoveFile={() => {
                                                setValidationErrors(undefined);
                                                setShowValidationErrorDialog(false);
                                                formik.setFieldValue('fileToUpload', undefined);
                                            }}
                                        />
                                        {validationErrors && (
                                            <div className="margin__bottom--medium">
                                                <InlineMessage
                                                    intent={InlineMessage.Intent.ERROR}
                                                    wrapText
                                                    label={
                                                        <>
                                                            <span>
                                                                One or more referrals was not
                                                                eligible for the selected value
                                                                case.
                                                            </span>
                                                            <br />
                                                            <span>
                                                                Please fix the errors and try
                                                                again.&nbsp;
                                                            </span>
                                                            <u
                                                                style={{ cursor: 'pointer' }}
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() =>
                                                                    setShowValidationErrorDialog(
                                                                        true,
                                                                    )
                                                                }
                                                                onKeyDown={(event) => {
                                                                    if (event.key === 'Enter') {
                                                                        setShowValidationErrorDialog(
                                                                            true,
                                                                        );
                                                                    }
                                                                }}
                                                            >
                                                                View Details
                                                            </u>
                                                        </>
                                                    }
                                                />
                                            </div>
                                        )}
                                    </>
                                }
                            />
                        );
                    }}
                </Formik>
            }
        />
    );
};
